import { createHttpApi, type Api } from "./client";
import type { EnvironmentName, RegionToken } from "./schemas";
import { consoleRegionScopes } from "./scopes";

export const USE_FIXTURES = import.meta.env.VITE_USE_FIXTURES === "1";

/** The single API instance. Control-plane paths are same-origin (`/app`, proxied to :4600 in dev). */
// The fixture engine (people, SQL, seeds) is only loaded in demo mode, never in the real build's path.
export const api: Api = USE_FIXTURES
  ? (await import("./fixtures/adapter")).createFixtureApi()
  : createHttpApi(import.meta.env.VITE_API_BASE ?? "");

// Region tokens live ≤15 min; reuse one until a minute before expiry.
const regionTokens = new Map<string, Promise<RegionToken>>();

const projectRoles = new Map<string, string>();
/** The project layout records the viewer's role so region tokens can be narrowed to it. */
export function rememberProjectRole(projectId: string, role: string): void {
  if (projectRoles.get(projectId) !== role) {
    projectRoles.set(projectId, role);
    for (const k of [...regionTokens.keys()]) if (k.startsWith(`${projectId}:`)) regionTokens.delete(k);
  }
}

export function regionTokenFor(projectId: string, environment: EnvironmentName): Promise<RegionToken> {
  const key = `${projectId}:${environment}`;
  const cached = regionTokens.get(key);
  if (cached) {
    return cached.then((rt) => {
      if (new Date(rt.expires_at).getTime() - Date.now() > 60_000) return rt;
      regionTokens.delete(key);
      return regionTokenFor(projectId, environment);
    });
  }
  const role = projectRoles.get(projectId);
  const fresh = api.regionToken(projectId, environment, role ? consoleRegionScopes(role) : undefined);
  regionTokens.set(key, fresh);
  fresh.catch(() => regionTokens.delete(key));
  return fresh;
}

export const clearRegionTokens = () => regionTokens.clear();

export type { Api } from "./client";
export * from "./http";
